import React, { useEffect, useState } from 'react';
import { Activity, Building2, BookOpen, Image, CalendarDays, Quote, Layers, TrendingUp, Users, MapPin } from 'lucide-react';
import { useData } from '@/contexts/DataContext';
import { useNavigate } from 'react-router-dom';

const DashboardHome = () => {
  const { 
    properties, 
    apartments, 
    blogPosts, 
    heroSlides, 
    testimonials, 
    galleryImages, 
    galleryCategories,
    propertyCategories,
    listingTeam,
    loading 
  } = useData();
  
  const navigate = useNavigate();
  const [recentActivity, setRecentActivity] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    // Update time every second
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Generate recent activity based on actual data
    const activities = [
      {
        id: 1,
        title: 'New Property Listed',
        description: `${properties.length} total properties available`,
        time: '2 hours ago',
        icon: Building2,
        color: 'text-lime-500'
      },
      {
        id: 2,
        title: 'Blog Content Updated',
        description: `${blogPosts.filter(b => b.published).length} published posts`,
        time: '5 hours ago',
        icon: BookOpen,
        color: 'text-blue-500'
      },
      {
        id: 3,
        title: 'Gallery Updated',
        description: `${galleryImages.length} images in gallery`,
        time: '1 day ago',
        icon: Image,
        color: 'text-purple-500'
      },
      {
        id: 4,
        title: 'Team Activity',
        description: `${listingTeam.length} active team members`,
        time: '2 days ago',
        icon: Users,
        color: 'text-orange-500'
      },
      {
        id: 5,
        title: 'Property Categories',
        description: `${propertyCategories.length} property categories`,
        time: '3 days ago',
        icon: MapPin,
        color: 'text-indigo-500'
      },
      {
        id: 6,
        title: 'Testimonials Added',
        description: `${testimonials.length} customer testimonials`,
        time: '1 week ago',
        icon: Quote,
        color: 'text-pink-500'
      }
    ];
    setRecentActivity(activities);
  }, [properties.length, blogPosts.length, galleryImages.length, listingTeam.length, propertyCategories.length, testimonials.length]);

  const metrics = [
    { 
      label: 'Properties', 
      value: properties.length, 
      icon: Building2, 
      accent: 'from-blue-400 to-blue-600',
      bgColor: 'bg-blue-50',
      change: '+12%',
      link: '/dashboard/properties',
      description: 'Manage property listings'
    },
    { 
      label: 'Apartments', 
      value: apartments.length, 
      icon: Layers, 
      accent: 'from-green-400 to-green-600',
      bgColor: 'bg-green-50',
      change: '+8%',
      link: '/dashboard/apartments',
      description: 'Apartment management'
    },
    { 
      label: 'Blog Posts', 
      value: blogPosts.filter(b => b.published).length, 
      icon: BookOpen, 
      accent: 'from-red-400 to-red-600',
      bgColor: 'bg-red-50',
      change: '+15%',
      link: '/dashboard/blogs',
      description: 'Content management'
    },
    { 
      label: 'Hero Slides', 
      value: heroSlides.length, 
      icon: Image, 
      accent: 'from-yellow-400 to-yellow-600',
      bgColor: 'bg-yellow-50',
      change: '0%',
      link: '/dashboard/hero-slides',
      description: 'Hero carousel management'
    },
    { 
      label: 'Gallery Images', 
      value: galleryImages.length, 
      icon: Image, 
      accent: 'from-blue-500 to-blue-700',
      bgColor: 'bg-blue-100',
      change: '+23%',
      link: '/dashboard/gallery',
      description: 'Image gallery management'
    },
    { 
      label: 'Testimonials', 
      value: testimonials.length, 
      icon: Quote, 
      accent: 'from-green-500 to-green-700',
      bgColor: 'bg-green-100',
      change: '+5%',
      link: '/dashboard/testimonials',
      description: 'Customer testimonials'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-[#81be00] to-[#6a9e00] rounded-2xl p-6 text-white">
        <h1 className="text-3xl font-bold">Welcome to Nyinimu Admin</h1>
        <p className="mt-2 text-white/90">
          Here's what's happening with your property management system today.
        </p>
        <div className="mt-4 flex items-center gap-4 text-sm">
          <span className="flex items-center gap-2">
            <div className="h-2 w-2 bg-green-400 rounded-full"></div>
            System Online
          </span>
          <span>Last updated: {currentTime.toLocaleTimeString()}</span>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {metrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <button
              key={metric.label}
              onClick={() => navigate(metric.link)}
              className="group relative overflow-hidden rounded-2xl border border-stone-200 bg-white p-6 shadow-sm transition-all hover:shadow-lg hover:shadow-black/5 hover:scale-105 hover:border-stone-300 text-left w-full"
            >
              <div className={`absolute inset-0 ${metric.bgColor} opacity-50 group-hover:opacity-70 transition-opacity`}></div>
              <div className={`absolute inset-0 bg-gradient-to-br ${metric.accent} opacity-5 group-hover:opacity-10 transition-opacity`}></div>
              <div className="relative flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-stone-600">{metric.label}</p>
                  <p className="mt-2 text-3xl font-bold text-stone-900">
                    {loading ? '...' : metric.value.toLocaleString()}
                  </p>
                  <p className="mt-1 text-xs text-stone-500">{metric.description}</p>
                  <div className="mt-2 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3 text-green-500" />
                    <span className="text-xs font-medium text-green-600">{metric.change}</span>
                    <span className="text-xs text-stone-500">vs last month</span>
                  </div>
                </div>
                <div className={`inline-flex rounded-xl bg-gradient-to-br ${metric.accent} p-3 text-white shadow-sm group-hover:scale-110 transition-transform`}>
                  <Icon className="h-5 w-5" />
                </div>
              </div>
              <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="text-xs text-stone-400">Click to manage →</div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Activity */}
        <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-stone-900">Recent Activity</h2>
              <p className="text-sm text-stone-500">Latest updates and changes</p>
            </div>
            <Activity className="h-5 w-5 text-[#81be00]" />
          </div>
          <div className="space-y-4">
            {recentActivity.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.id} className="flex items-start gap-4 rounded-xl border border-stone-100 p-4 hover:bg-stone-50 transition-colors">
                  <div className={`inline-flex rounded-lg bg-stone-100 p-2 ${item.color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-stone-800 truncate">{item.title}</p>
                    <p className="text-sm text-stone-500 truncate">{item.description}</p>
                  </div>
                  <span className="text-xs text-stone-400 whitespace-nowrap">{item.time}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Actions & System Info */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="rounded-2xl border border-stone-200 bg-gradient-to-br from-white to-stone-50 p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-stone-900 mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <button 
                onClick={() => window.open('/calculator', '_blank')}
                className="w-full flex items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-[#81be00] to-[#6a9e00] px-4 py-4 text-sm font-medium text-white hover:shadow-lg hover:scale-105 transition-all"
              >
                <Activity className="h-5 w-5" />
                Nyinimu Calculator
              </button>
              <div className="grid grid-cols-2 gap-3">
                <button 
                  onClick={() => navigate('/dashboard/properties')}
                  className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm font-medium text-stone-700 hover:bg-stone-50 hover:shadow-md transition-colors"
                >
                  <Building2 className="h-4 w-4" />
                  Add Property
                </button>
                <button 
                  onClick={() => navigate('/dashboard/blogs')}
                  className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm font-medium text-stone-700 hover:bg-stone-50 hover:shadow-md transition-colors"
                >
                  <BookOpen className="h-4 w-4" />
                  New Blog Post
                </button>
                <button 
                  onClick={() => navigate('/dashboard/gallery')}
                  className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm font-medium text-stone-700 hover:bg-stone-50 hover:shadow-md transition-colors"
                >
                  <Image className="h-4 w-4" />
                  Upload Image
                </button>
                <button 
                  onClick={() => navigate('/dashboard/team')}
                  className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm font-medium text-stone-700 hover:bg-stone-50 hover:shadow-md transition-colors"
                >
                  <Users className="h-4 w-4" />
                  Manage Team
                </button>
              </div>
            </div>
          </div>

          {/* System Status */}
          <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-stone-900 mb-4">System Status</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-stone-600">Database</span>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-green-600">Online</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-stone-600">Storage</span>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-green-600">Connected</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-stone-600">API Status</span>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-green-600">Healthy</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-stone-600">Last Backup</span>
                <span className="text-sm text-stone-500">2 hours ago</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Overview */}
      <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-stone-900 mb-6">Performance Overview</h2>
        <div className="grid gap-6 md:grid-cols-4">
          <button
            onClick={() => navigate('/dashboard/properties')}
            className="text-center p-4 rounded-xl bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors hover:scale-105"
          >
            <div className="mx-auto h-16 w-16 rounded-full bg-blue-200 flex items-center justify-center mb-3">
              <Building2 className="h-8 w-8 text-blue-700" />
            </div>
            <p className="text-2xl font-bold text-stone-900">{properties.length}</p>
            <p className="text-sm text-stone-600">Total Properties</p>
          </button>
          <button
            onClick={() => navigate('/dashboard/apartments')}
            className="text-center p-4 rounded-xl bg-green-50 border border-green-200 hover:bg-green-100 transition-colors hover:scale-105"
          >
            <div className="mx-auto h-16 w-16 rounded-full bg-green-200 flex items-center justify-center mb-3">
              <Layers className="h-8 w-8 text-green-700" />
            </div>
            <p className="text-2xl font-bold text-stone-900">{apartments.length}</p>
            <p className="text-sm text-stone-600">Apartments</p>
          </button>
          <button
            onClick={() => navigate('/dashboard/blogs')}
            className="text-center p-4 rounded-xl bg-red-50 border border-red-200 hover:bg-red-100 transition-colors hover:scale-105"
          >
            <div className="mx-auto h-16 w-16 rounded-full bg-red-200 flex items-center justify-center mb-3">
              <BookOpen className="h-8 w-8 text-red-700" />
            </div>
            <p className="text-2xl font-bold text-stone-900">{blogPosts.length}</p>
            <p className="text-sm text-stone-600">Blog Posts</p>
          </button>
          <button
            onClick={() => navigate('/dashboard/hero-slides')}
            className="text-center p-4 rounded-xl bg-yellow-50 border border-yellow-200 hover:bg-yellow-100 transition-colors hover:scale-105"
          >
            <div className="mx-auto h-16 w-16 rounded-full bg-yellow-200 flex items-center justify-center mb-3">
              <Image className="h-8 w-8 text-yellow-700" />
            </div>
            <p className="text-2xl font-bold text-stone-900">{heroSlides.length}</p>
            <p className="text-sm text-stone-600">Hero Slides</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
